import { observer } from "mobx-react-lite";
import { useState } from "react";
import styled from "@emotion/styled";
import {
  MDSButton,
  MDSButtonVariant,
  MDSButtonSlotName,
  MDSIcon,
  MDSIconButton,
  MDSIconKind,
  MDSListBox,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
  MDSMenuContentItemKind,
  MDSIconButtonSize,
} from "@/mds";
import { MDSActionMenuDropdown } from "@/mds/components/menu/action-menu/action-menu-dropdown";
import { MDSMenuDropdownSlotName } from "@/mds";
import { TemplateObservable } from "@/store/templates/TemplateObservable";
import { SettingsModalTemplatesPageStore } from "@/store/settings-modal/SettingsModalTemplatesPageStore";
import { ShareSheetEntityKind } from "@/components/modal/share-sheet/types";
import { EventContext } from "@/domains/metrics/context";
import { useAppStore } from "@/store";
import { SettingsTabId } from "@/store/pages/SettingsPageStore/types";
import {
  SettingsIntegrationPageWrapper,
  SettingsIntegrationScrollableArea,
} from "@/pages/settings/components/settings-integration-layout";

interface SettingsTemplatesListViewProps {
  pageStore: SettingsModalTemplatesPageStore;
}

export const SettingsTemplatesListView = observer<SettingsTemplatesListViewProps>(
  function SettingsTemplatesListView({ pageStore }) {
    const { store } = useAppStore();

    const hasMyTemplates = pageStore.myTemplates.length > 0;
    const hasCalendarTemplates = pageStore.calendarTemplates.length > 0;
    const hasSharedTemplates = pageStore.sharedWithMeTemplates.length > 0;
    const isEmpty = !hasMyTemplates && !hasCalendarTemplates && !hasSharedTemplates;

    return (
      <SettingsIntegrationPageWrapper>
        <Header>
          <MDSText
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Secondary}
          >
            General
          </MDSText>
          <TitleRow>
            <MDSText variant={MDSTextVariant.HeaderL}>Templates</MDSText>
            <MDSButton
              variant={MDSButtonVariant.Default}
              onClick={() => pageStore.createTemplate()}
            >
              <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Plus} />
              New template
            </MDSButton>
          </TitleRow>
        </Header>

        <SettingsIntegrationScrollableArea>
          {isEmpty && (
            <EmptyState>
              <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
                Your templates and templates shared with you appear here.
              </MDSText>
            </EmptyState>
          )}

          {hasMyTemplates && (
            <Section>
              <SectionHeader>
                <MDSText
                  variant={MDSTextVariant.TextXs}
                  weight={MDSTextWeight.SemiBold}
                  color={MDSTextColor.Secondary}
                >
                  MY TEMPLATES
                </MDSText>
                <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
                  Take consistent notes with templates.
                </MDSText>
              </SectionHeader>
              <StyledListBox
                aria-label="My templates"
                items={pageStore.myTemplates.map(template =>
                  templateToListBoxItem(template, pageStore)
                )}
              />
            </Section>
          )}

          {hasCalendarTemplates && (
            <Section>
              <SectionHeader>
                <MDSText
                  variant={MDSTextVariant.TextXs}
                  weight={MDSTextWeight.SemiBold}
                  color={MDSTextColor.Secondary}
                >
                  CALENDAR AND MEETINGS
                </MDSText>
                <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
                  Template used by default for meeting notes from the{" "}
                  <CalendarLink
                    onClick={() =>
                      store.settingsModal.navigateToTab(SettingsTabId.CalendarAndMeetingsPage)
                    }
                  >
                    Calendar and meetings
                  </CalendarLink>{" "}
                  integration
                </MDSText>
              </SectionHeader>
              <StyledListBox
                aria-label="Calendar and meetings templates"
                items={pageStore.calendarTemplates.map(template =>
                  calendarTemplateToListBoxItem(template, pageStore)
                )}
              />
            </Section>
          )}

          {hasSharedTemplates && (
            <Section>
              <SectionHeader>
                <MDSText
                  variant={MDSTextVariant.TextXs}
                  weight={MDSTextWeight.SemiBold}
                  color={MDSTextColor.Secondary}
                >
                  SHARED WITH ME
                </MDSText>
              </SectionHeader>
              <StyledListBox
                aria-label="Shared with me templates"
                items={pageStore.sharedWithMeTemplates.map(template =>
                  templateToListBoxItem(template, pageStore)
                )}
              />
            </Section>
          )}
        </SettingsIntegrationScrollableArea>
      </SettingsIntegrationPageWrapper>
    );
  }
);

function templateToListBoxItem(
  template: TemplateObservable,
  pageStore: SettingsModalTemplatesPageStore
) {
  return {
    id: template.id,
    textValue: template.label,
    leftAddon: (
      <LeftIconWrapper>
        <MDSIcon kind={MDSIconKind.AlignLeft} />
      </LeftIconWrapper>
    ),
    children: <MDSText variant={MDSTextVariant.TextS}>{template.label}</MDSText>,
    rightAddon: <TemplateItemActions template={template} pageStore={pageStore} />,
  };
}

/** Actions for a regular template item — tracks dropdown open state to stay visible */
const TemplateItemActions = ({
  template,
  pageStore,
}: {
  template: TemplateObservable;
  pageStore: SettingsModalTemplatesPageStore;
}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <ItemActions style={menuOpen ? { opacity: 1 } : undefined}>
      <MDSIconButton
        data-test-id={`edit-template-${template.id}`}
        iconKind={MDSIconKind.Pen}
        size={MDSIconButtonSize.Small}
        onClick={() => pageStore.navigateToTemplateEdit(template.id)}
      />
      <MDSActionMenuDropdown
        contentList={[
          {
            id: `share-${template.id}`,
            kind: MDSMenuContentItemKind.Item,
            icon: MDSIconKind.Share,
            label: "Share",
            onClick: () =>
              pageStore.shareSheetModal.open({
                id: template.id,
                entityKind: ShareSheetEntityKind.Template,
                eventContext: EventContext.TemplatesMultiselectActions,
              }),
          },
          ...(template.isOwnedByMe
            ? [
                {
                  id: `delete-${template.id}`,
                  kind: MDSMenuContentItemKind.Item as const,
                  icon: MDSIconKind.Trash,
                  label: "Delete",
                  onClick: () => pageStore.deleteTemplateModal.open({ templateId: template.id }),
                },
              ]
            : []),
        ]}
        placement="bottom right"
        onOpenChange={setMenuOpen}
      >
        <MDSIconButton
          slot={MDSMenuDropdownSlotName.Trigger}
          iconKind={MDSIconKind.More}
          size={MDSIconButtonSize.Small}
        />
      </MDSActionMenuDropdown>
    </ItemActions>
  );
};

/** Calendar templates only get the edit pencil — no "..." menu */
function calendarTemplateToListBoxItem(
  template: TemplateObservable,
  pageStore: SettingsModalTemplatesPageStore
) {
  return {
    id: template.id,
    textValue: template.label,
    leftAddon: (
      <LeftIconWrapper>
        <MDSIcon kind={MDSIconKind.AlignLeft} />
      </LeftIconWrapper>
    ),
    children: (
      <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Tertiary}>
        {template.label}
      </MDSText>
    ),
    rightAddon: (
      <ItemActions className="item-actions">
        <MDSIconButton
          data-test-id={`edit-template-${template.id}`}
          iconKind={MDSIconKind.Pen}
          size={MDSIconButtonSize.Small}
          onClick={() => pageStore.navigateToTemplateEdit(template.id)}
        />
      </ItemActions>
    ),
  };
}

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => `${theme.spacing[5]} ${theme.spacing[7]} ${theme.spacing[3]}`};

  @container mds-panel (max-width: 750px) {
    padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]} ${theme.spacing[1]}`};
  }
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 700px;
`;

const EmptyState = styled.div`
  padding: 0 ${({ theme }) => theme.spacing[7]};

  @container mds-panel (max-width: 750px) {
    padding: 0 ${({ theme }) => theme.spacing[4]};
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: 0 ${({ theme }) => theme.spacing[7]};
  max-width: 700px;

  @container mds-panel (max-width: 750px) {
    padding: 0 ${({ theme }) => theme.spacing[4]};
  }
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["0.5"]};
`;

const LeftIconWrapper = styled.div`
  display: flex;
  align-items: center;
  align-self: center;
`;

/**
 * List box overrides:
 * - No gap between items (items sit together as a cohesive box)
 * - Left icon centered vertically
 * - Action buttons hidden by default, visible on hover or when dropdown is open
 */
const StyledListBox = styled(MDSListBox)`
  gap: ${({ theme }) => theme.spacing[4]};

  [slot="left-addon"] {
    align-self: center;
  }
`;

/**
 * Hidden by default. Becomes visible when:
 * 1. The parent ListBoxItem is hovered (via CSS)
 * 2. The dropdown menu is open (via inline style override from TemplateItemActions)
 *
 * We use a negative margin trick with [data-rac] to target the react-aria
 * ListBoxItem ancestor reliably.
 */
const ItemActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  opacity: 0;
  transition: opacity 0.15s ease;

  /* react-aria ListBoxItem is the closest ancestor with this selector */
  [role="option"]:hover & {
    opacity: 1;
  }
`;

const CalendarLink = styled.span`
  color: ${({ theme }) => theme.colors.text.link.default};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;
